import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import history from "../../utils/history";
import urls from "../../global/constants/UrlConstants";
import YhTable, { YhTableCellParams, YhTableColumn } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhButton from "../../ui/YhButton";
import YhSelect, { buildStageSelectGroups } from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";
import {
  selectActiveOrganizationId,
  selectMemberships,
} from "../../redux/authSlice";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  fetchAuthKeyTemplates,
  listTenantOrganizations,
} from "../../services/organizationService";
import {
  AudienceContact,
  createWhatsAppBroadcast,
  parseWhatsAppAudienceFile,
  previewMarketingLeads,
  listWhatsAppAudienceForms,
} from "../../services/whatsappMarketingService";
import { listBulkJobs } from "../../services/bulkJobService";
import { getProjectDetails } from "../Project/components/ShowAll/ShowAllService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { getAllSource } from "../AllLeads/components/ShowAll/ShowAllService";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";
import MarketingAccessGate from "./MarketingAccessGate";

const Field = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`flex w-full min-w-0 flex-col justify-start ${className}`}
  >
    {children}
  </div>
);

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-[#EEF0F5] bg-white p-4 md:p-5">
    <div className="mb-4 border-b border-[#F3F4F6] pb-3">
      <h2 className="text-sm font-semibold text-[#111827]">{title}</h2>
      {subtitle ? (
        <p className="mt-0.5 text-xs text-[#6B7280]">{subtitle}</p>
      ) : null}
    </div>
    {children}
  </section>
);

const WhatsAppBroadcastCreatePage = () => {
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const memberships = useSelector(selectMemberships);

  const [name, setName] = useState("");
  const [organizationIds, setOrganizationIds] = useState<string[]>(
    activeOrgId && activeOrgId !== "all" ? [String(activeOrgId)] : []
  );
  const [orgOptions, setOrgOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [stages, setStages] = useState<string[]>([]);
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState("");
  /** Selected dataset keys (setName or job:<id>) */
  const [bulkSetKeys, setBulkSetKeys] = useState<string[]>([]);
  const [bulkSetOptions, setBulkSetOptions] = useState<
    { label: string; value: string; jobIds: string[]; setName: string }[]
  >([]);
  const [audienceContacts, setAudienceContacts] = useState<AudienceContact[]>(
    []
  );
  const [audienceFileName, setAudienceFileName] = useState("");
  /** file_only | crm_match | registration | feedback */
  const [audienceMode, setAudienceMode] = useState<
    "file_only" | "crm_match" | "registration" | "feedback"
  >("file_only");
  const [submissionFormId, setSubmissionFormId] = useState("");
  const [submissionFormOptions, setSubmissionFormOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [audienceStats, setAudienceStats] = useState<{
    fileRows?: number;
    matched?: number;
    unmatched?: number;
    mode?: string;
  } | null>(null);
  /** instant (default) | scheduled — times are India Standard Time */
  const [runMode, setRunMode] = useState<"instant" | "scheduled">("instant");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [parsingAudience, setParsingAudience] = useState(false);
  const [stageData, setStageData] = useState<any[]>([]);
  const [sourceOptions, setSourceOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [templates, setTemplates] = useState<
    { wid: string; name: string; label: string }[]
  >([]);
  const [templateMode, setTemplateMode] = useState<
    "single" | "by_registration"
  >("single");
  const [templateWid, setTemplateWid] = useState("");
  const [templateWidRegistered, setTemplateWidRegistered] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [previewRows, setPreviewRows] = useState<any[]>([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatched, setSelectAllMatched] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const primaryOrgId = organizationIds[0] || "";

  useEffect(() => {
    if (activeOrgId && activeOrgId !== "all" && !organizationIds.length) {
      setOrganizationIds([String(activeOrgId)]);
    }
  }, [activeOrgId, organizationIds.length]);

  useEffect(() => {
    (async () => {
      try {
        const [stageRes, sourceRes] = await Promise.all([
          getStage(),
          getAllSource(),
        ]);
        setStageData(Array.isArray(stageRes?.data) ? stageRes.data : []);
        const sources = Array.isArray(sourceRes?.data) ? sourceRes.data : [];
        setSourceOptions(
          sources
            .map((s: any) => {
              const name = String(s?.source_name || s?.name || "").trim();
              if (!name) return null;
              return { label: name, value: name };
            })
            .filter(Boolean) as { label: string; value: string }[]
        );
      } catch {
        setStageData([]);
        setSourceOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const toOptions = (list: any[]) => {
        const seen = new Set<string>();
        const out: { label: string; value: string }[] = [];
        (list || []).forEach((o: any) => {
          const id = String(o?._id || o?.id || "").trim();
          if (
            !id ||
            id === "undefined" ||
            id === "all" ||
            id === "*" ||
            seen.has(id)
          ) {
            return;
          }
          seen.add(id);
          out.push({
            value: id,
            label:
              o?.name ||
              o?.brandName ||
              o?.legalName ||
              o?.slug ||
              id,
          });
        });
        return out;
      };

      try {
        const res = await listTenantOrganizations();
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.items)
          ? res.items
          : [];
        const fromApi = toOptions(list);
        if (fromApi.length) {
          setOrgOptions(fromApi);
          return;
        }
      } catch {
        /* fall through */
      }
      setOrgOptions(
        toOptions(
          (memberships || []).map((m: any) => ({
            _id: m?.organizationId || m?.organization?._id,
            name:
              m?.organization?.name ||
              m?.organization?.brandName ||
              m?.organizationName,
            brandName: m?.organization?.brandName,
            legalName: m?.organization?.legalName,
            slug: m?.organization?.slug,
          }))
        )
      );
    })();
  }, [memberships]);

  // Never keep header "all" as a selected org value (chips would look empty)
  useEffect(() => {
    setOrganizationIds((prev) => {
      const next = (prev || []).filter(
        (id) => id && id !== "all" && id !== "*" && id !== "undefined"
      );
      return next.length === prev.length ? prev : next;
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProjectDetails({
          pageNumber: 0,
          pageSize: 200,
          search: "",
          status: 1,
        });
        setAllProjects(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setAllProjects([]);
      }
    })();
  }, []);

  const projectOptions = useMemo(() => {
    const orgSet = new Set(organizationIds.map(String).filter(Boolean));
    return (allProjects || [])
      .filter((p: any) => {
        if (!orgSet.size) return false;
        const oid = String(p.organizationId || "");
        if (!oid) return true;
        return orgSet.has(oid);
      })
      .map((p: any) => ({
        label: p.project_name || p.name || p.project_uid || String(p._id),
        value: String(p._id),
      }));
  }, [allProjects, organizationIds]);

  // Drop project selections that no longer belong to selected orgs
  useEffect(() => {
    const allowed = new Set(projectOptions.map((p) => String(p.value)));
    setProjectIds((prev) => {
      const next = prev.filter((id) => allowed.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [projectOptions]);

  const stageGroups = useMemo(
    () => buildStageSelectGroups(stageData || []),
    [stageData]
  );
  const stageOptions = useMemo(
    () =>
      stageGroups.flatMap((g) =>
        (g.options || []).map((o) => ({
          label: `${g.label} › ${o.label}`,
          value: String(o.value),
        }))
      ),
    [stageGroups]
  );

  useEffect(() => {
    setBulkSetKeys([]);
  }, [organizationIds.join("|")]);

  useEffect(() => {
    (async () => {
      if (!organizationIds.length) {
        setBulkSetOptions([]);
        return;
      }
      try {
        const orgSet = new Set(organizationIds.map(String));
        const results = await Promise.all(
          organizationIds.map((orgId) =>
            listBulkJobs({
              type: "leads",
              limit: 100,
              organizationId: orgId,
              status: "completed,partial",
            })
          )
        );
        const items: any[] = [];
        const seenJob = new Set<string>();
        for (const res of results) {
          const list = Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.items)
            ? res.items
            : [];
          for (const j of list) {
            const id = String(j?._id || "");
            if (!id || seenJob.has(id)) continue;
            const jOrg = String(
              j.organizationId ||
                (Array.isArray(j.organizationIds) && j.organizationIds[0]) ||
                ""
            );
            if (jOrg && !orgSet.has(jOrg)) continue;
            seenJob.add(id);
            items.push(j);
          }
        }

        // Group by dataset name so reused sets appear once
        const bySet = new Map<
          string,
          { label: string; value: string; jobIds: string[]; setName: string; success: number }
        >();
        for (const j of items) {
          const id = String(j._id);
          const ok = Number(j.totals?.success ?? j.totals?.unique ?? 0) || 0;
          if (ok <= 0) continue;
          const setName = String(j.setName || "").trim();
          const key = setName
            ? `set:${setName.toLowerCase()}`
            : `job:${id}`;
          const displayName =
            setName ||
            j.subSource ||
            j.originalFileName ||
            `Upload ${id.slice(-6)}`;
          const statusTag =
            String(j.status) === "partial" ? " · partial" : "";
          const existing = bySet.get(key);
          if (existing) {
            existing.jobIds.push(id);
            existing.success += ok;
          } else {
            bySet.set(key, {
              value: key,
              setName: setName || displayName,
              jobIds: [id],
              success: ok,
              label: "",
              statusTag,
            } as any);
          }
        }
        const options = Array.from(bySet.values()).map((o: any) => ({
          ...o,
          label: `${o.setName} · ${o.success} leads${o.statusTag || ""}${
            o.jobIds.length > 1 ? ` · ${o.jobIds.length} uploads` : ""
          }`,
        }));
        options.sort((a, b) =>
          a.setName.localeCompare(b.setName, undefined, {
            sensitivity: "base",
          })
        );
        setBulkSetOptions(options);
        setBulkSetKeys((prev) =>
          prev.filter((k) => options.some((o) => o.value === k))
        );
      } catch {
        setBulkSetOptions([]);
      }
    })();
  }, [organizationIds.join("|")]);

  const selectedBulkJobIds = useMemo(() => {
    const ids = new Set<string>();
    for (const key of bulkSetKeys) {
      const opt = bulkSetOptions.find((o) => o.value === key);
      (opt?.jobIds || []).forEach((id) => ids.add(String(id)));
    }
    return Array.from(ids);
  }, [bulkSetKeys, bulkSetOptions]);

  const selectedBulkSetNames = useMemo(() => {
    const names = new Set<string>();
    for (const key of bulkSetKeys) {
      const opt = bulkSetOptions.find((o) => o.value === key);
      const n = String(opt?.setName || "").trim();
      if (n && key.startsWith("set:")) names.add(n);
    }
    return Array.from(names);
  }, [bulkSetKeys, bulkSetOptions]);

  const loadTemplates = useCallback(async (orgId: string) => {
    setTemplates([]);
    setTemplateWid("");
    setTemplateWidRegistered("");
    if (!orgId) return;
    setLoadingTemplates(true);
    try {
      const res = await fetchAuthKeyTemplates(orgId, "whatsapp");
      const raw = res?.data || [];
      const list = (Array.isArray(raw) ? raw : [])
        .map((t: any) => {
          const wid = String(t.wid || t.id || t.template_id || "");
          const tName = String(t.name || t.template_name || wid);
          if (!wid) return null;
          return { wid, name: tName, label: `${tName} (${wid})` };
        })
        .filter(Boolean) as { wid: string; name: string; label: string }[];
      setTemplates(list);
      if (!list.length) {
        openErrorNotification(
          "No WhatsApp templates for this organization. Configure Engage under Integrations."
        );
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (primaryOrgId) loadTemplates(primaryOrgId);
    else {
      setTemplates([]);
      setTemplateWid("");
    }
  }, [primaryOrgId, loadTemplates]);

  const loadPreview = useCallback(async () => {
    if (!organizationIds.length) {
      setPreviewRows([]);
      setPreviewTotal(0);
      return;
    }
    if (audienceMode === "file_only" && !audienceContacts.length) {
      setPreviewRows([]);
      setPreviewTotal(0);
      setAudienceStats(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await previewMarketingLeads({
        organizationIds,
        projectIds: projectIds.length ? projectIds : undefined,
        bulkJobIds:
          audienceMode === "crm_match" && selectedBulkJobIds.length
            ? selectedBulkJobIds
            : undefined,
        bulkSetNames:
          audienceMode === "crm_match" && selectedBulkSetNames.length
            ? selectedBulkSetNames
            : undefined,
        audienceContacts:
          audienceMode === "file_only" && audienceContacts.length
            ? audienceContacts
            : undefined,
        audienceMode,
        filters: {
          stages:
            audienceMode === "crm_match" && stages.length ? stages : undefined,
          source: audienceMode === "crm_match" ? source : undefined,
          search: audienceMode === "crm_match" ? search : undefined,
          registrationStatus:
            audienceMode === "crm_match" && registrationStatus
              ? registrationStatus
              : undefined,
          bulkJobIds:
            audienceMode === "crm_match" && selectedBulkJobIds.length
              ? selectedBulkJobIds
              : undefined,
          bulkSetNames:
            audienceMode === "crm_match" && selectedBulkSetNames.length
              ? selectedBulkSetNames
              : undefined,
          formId:
            (audienceMode === "registration" || audienceMode === "feedback") &&
            submissionFormId
              ? submissionFormId
              : undefined,
          audienceMode,
        },
        page,
        pageSize,
      });
      setPreviewRows(Array.isArray(res?.data) ? res.data : []);
      setPreviewTotal(res?.metadata?.[0]?.total || 0);
      const aud = res?.metadata?.[0]?.audience;
      if (aud) {
        setAudienceStats({
          fileRows: aud.fileRows,
          matched: aud.matched,
          unmatched: aud.unmatched,
          mode: aud.mode,
        });
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to preview leads");
    } finally {
      setLoadingPreview(false);
    }
  }, [
    organizationIds,
    projectIds,
    selectedBulkJobIds,
    selectedBulkSetNames,
    audienceContacts,
    audienceMode,
    submissionFormId,
    stages,
    source,
    search,
    registrationStatus,
    page,
    pageSize,
  ]);

  useEffect(() => {
    if (
      (audienceMode !== "registration" && audienceMode !== "feedback") ||
      !organizationIds.length
    ) {
      setSubmissionFormOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await listWhatsAppAudienceForms({
          organizationIds,
          formType: audienceMode,
        });
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setSubmissionFormOptions(
          rows.map((f: any) => ({
            value: String(f._id),
            label: String(f.form_name || "Untitled"),
          }))
        );
      } catch (_) {
        if (!cancelled) setSubmissionFormOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [audienceMode, organizationIds]);

  const downloadSampleAudience = () => {
    // Same columns the parser expects: Phone (required), Name, Email (optional)
    const rows = [
      ["Phone", "Name", "Email"],
      ["919876543210", "Sample Customer", "sample@example.com"],
      ["9876543210", "Another Contact", ""],
      ["918888777666", "Site Visit Lead", "lead@example.com"],
    ];
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const s = String(cell ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "whatsapp-instant-trigger-sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onAudienceFile = async (file: File | null) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".csv") && !lower.endsWith(".xlsx")) {
      openErrorNotification("Upload a .csv or .xlsx file");
      return;
    }
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization first");
      return;
    }
    setParsingAudience(true);
    try {
      const res = await parseWhatsAppAudienceFile({
        file,
        organizationIds,
        projectIds: projectIds.length ? projectIds : undefined,
      });
      const contacts = Array.isArray(res?.data?.contacts)
        ? res.data.contacts
        : [];
      if (!contacts.length) {
        openErrorNotification(
          "No phone numbers found. Use a Phone / Mobile column."
        );
        return;
      }
      setAudienceContacts(contacts);
      setAudienceFileName(res?.data?.fileName || file.name);
      const match = res?.data?.match;
      setAudienceStats(
        match
          ? {
              fileRows: match.totalContacts,
              matched: match.matchedCount,
              unmatched: match.unmatchedCount,
            }
          : { fileRows: contacts.length }
      );
      setPage(0);
      setSelectAllMatched(true);
      setSelectedIds(new Set());
      openSuccessNotification(
        audienceMode === "file_only"
          ? `Loaded ${contacts.length} phones — will message without adding CRM leads`
          : match
            ? `Matched ${match.matchedCount} of ${match.totalContacts} phones to CRM leads`
            : `Loaded ${contacts.length} phones from file`
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to parse audience file");
    } finally {
      setParsingAudience(false);
    }
  };

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const toggleRow = (id: string) => {
    setSelectAllMatched(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageAllSelected = useMemo(
    () =>
      previewRows.length > 0 &&
      previewRows.every((r) => selectedIds.has(String(r._id))),
    [previewRows, selectedIds]
  );

  const columns: YhTableColumn[] = [
    {
      headerName: (
        <input
          type="checkbox"
          className="h-4 w-4 accent-[#A02448]"
          checked={selectAllMatched || pageAllSelected}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectAllMatched(true);
              setSelectedIds(new Set());
            } else {
              setSelectAllMatched(false);
              setSelectedIds(new Set());
            }
          }}
        />
      ) as any,
      field: "_select",
      width: 52,
      renderCell: (params: YhTableCellParams) => {
        const id = String(params.row._id);
        const checked = selectAllMatched || selectedIds.has(id);
        return (
          <input
            type="checkbox"
            className="h-4 w-4 accent-[#A02448]"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleRow(id)}
          />
        );
      },
    },
    {
      headerName: "Lead",
      field: "leadName",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-[#111827]">
            {params.row.leadName || "—"}
          </span>
          <span className="truncate text-xs text-[#6B7280]">
            {(() => {
              const phone = formatPhoneKeepingCountryCode(
                resolveLeadPhoneForDisplay(params.row)
              );
              return phone !== "—" ? phone : params.row.leadEmail || "—";
            })()}
          </span>
        </div>
      ),
    },
    {
      headerName: "Stage",
      field: "stage",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] text-[#374151]">
          {params.row.stage || "—"}
        </span>
      ),
    },
    {
      headerName: "Source",
      field: "source",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] text-[#374151]">
          {params.row.source || "—"}
        </span>
      ),
    },
  ];

  const onSubmit = async () => {
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return;
    }
    if (!name.trim()) {
      openErrorNotification("Broadcast name is required");
      return;
    }
    if (!templateWid) {
      openErrorNotification(
        templateMode === "by_registration"
          ? "Select template for users who have NOT registered"
          : "Select a WhatsApp template"
      );
      return;
    }
    if (templateMode === "by_registration" && !templateWidRegistered) {
      openErrorNotification(
        "Select template for users who already registered"
      );
      return;
    }
    if (audienceMode === "file_only" && !audienceContacts.length) {
      openErrorNotification("Upload an XLSX/CSV to send without adding leads");
      return;
    }
    if (
      (audienceMode === "file_only" ||
        audienceMode === "registration" ||
        audienceMode === "feedback") &&
      templateMode === "by_registration"
    ) {
      openErrorNotification(
        "This audience needs a single template (registration split requires CRM leads)"
      );
      return;
    }
    if (!selectAllMatched && selectedIds.size === 0) {
      openErrorNotification(
        audienceMode === "crm_match"
          ? "Select at least one lead"
          : "Select at least one contact"
      );
      return;
    }
    if (!previewTotal) {
      openErrorNotification(
        audienceMode === "file_only"
          ? "No contacts in the uploaded file"
          : audienceMode === "registration"
            ? "No registration submissions with phones"
            : audienceMode === "feedback"
              ? "No feedback submissions with phones"
              : "No leads match the filters"
      );
      return;
    }
    if (runMode === "scheduled") {
      if (!scheduledLocal.trim()) {
        openErrorNotification("Pick a date and time in India (IST)");
        return;
      }
      // scheduledLocal is wall-clock IST from datetime-local
      const when = new Date(
        scheduledLocal.length === 16
          ? `${scheduledLocal}:00+05:30`
          : `${scheduledLocal}+05:30`
      );
      if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
        openErrorNotification("Scheduled time must be in the future (IST)");
        return;
      }
    }

    const tpl = templates.find((t) => t.wid === templateWid);
    const tplReg = templates.find((t) => t.wid === templateWidRegistered);
    setSubmitting(true);
    try {
      const res = await createWhatsAppBroadcast({
        name: name.trim(),
        organizationIds,
        projectIds: projectIds.length ? projectIds : undefined,
        templateWid,
        templateName: tpl?.name || "",
        templateMode,
        templateWidRegistered:
          templateMode === "by_registration"
            ? templateWidRegistered
            : undefined,
        templateNameRegistered:
          templateMode === "by_registration" ? tplReg?.name || "" : undefined,
        bulkJobIds: selectedBulkJobIds.length
          ? selectedBulkJobIds
          : undefined,
        bulkSetNames: selectedBulkSetNames.length
          ? selectedBulkSetNames
          : undefined,
        audienceContacts:
          audienceMode === "file_only" && audienceContacts.length
            ? audienceContacts
            : undefined,
        audienceMode,
        filters: {
          stages:
            audienceMode === "crm_match" && stages.length ? stages : undefined,
          source: audienceMode === "crm_match" ? source : undefined,
          search: audienceMode === "crm_match" ? search : undefined,
          registrationStatus:
            audienceMode === "crm_match" && registrationStatus
              ? registrationStatus
              : undefined,
          bulkJobIds:
            audienceMode === "crm_match" && selectedBulkJobIds.length
              ? selectedBulkJobIds
              : undefined,
          bulkSetNames:
            audienceMode === "crm_match" && selectedBulkSetNames.length
              ? selectedBulkSetNames
              : undefined,
          formId:
            (audienceMode === "registration" || audienceMode === "feedback") &&
            submissionFormId
              ? submissionFormId
              : undefined,
          audienceMode,
        },
        leadIds: selectAllMatched ? undefined : Array.from(selectedIds),
        maxRetries: 3,
        runMode,
        scheduledAt: runMode === "scheduled" ? scheduledLocal : undefined,
        timezone: "Asia/Kolkata",
      });
      if (!res?.success) {
        openErrorNotification(res?.message || "Failed to create broadcast");
        return;
      }
      openSuccessNotification(
        res.message ||
          (runMode === "scheduled"
            ? "Broadcast scheduled (IST)."
            : "Broadcast queued. You can leave this page.")
      );
      const id = res?.data?._id;
      if (id) {
        history.push(
          urls.whatsappMarketingDetailPath.replace(":id", String(id))
        );
      } else {
        history.push(urls.whatsappMarketingPath);
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to create broadcast");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingAccessGate>
    <div className="mx-auto flex h-full min-h-0 w-full max-w-[1200px] flex-col gap-5 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            className="mb-1 text-sm font-semibold text-[#A02448] hover:underline"
            onClick={() => history.push(urls.whatsappMarketingPath)}
          >
            ← Back to broadcasts
          </button>
          <h1 className="text-xl font-semibold tracking-tight text-[#111827]">
            WhatsApp Instant Trigger
          </h1>
          <p className="mt-1 max-w-xl text-sm text-[#6B7280]">
            Upload phones and send templates directly, or select CRM leads with
            stage filters. Track sent / failed on the broadcast detail page.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <YhButton
            variant="secondary"
            onClick={() => history.push(urls.whatsappMarketingPath)}
          >
            Cancel
          </YhButton>
          <YhButton variant="primary" loading={submitting} onClick={onSubmit}>
            {runMode === "scheduled" ? "Schedule broadcast" : "Start broadcast"}
          </YhButton>
        </div>
      </div>

      <Section
        title="Broadcast setup"
        subtitle="Organization is required (multi-select / Select all). Templates load from the first selected organization."
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field>
              <YhMultiSelect
                label="Organization"
                requiredMark
                showSelectAll
                clearable
                maxVisibleChips={2}
                placeholder="Select organizations…"
                searchPlaceholder="Search organizations…"
                options={orgOptions}
                value={organizationIds}
                onChange={(values) => {
                  setOrganizationIds(
                    values
                      .map(String)
                      .filter((id) => id && id !== "all" && id !== "*")
                  );
                  setPage(0);
                  setSelectAllMatched(true);
                  setSelectedIds(new Set());
                }}
              />
              {organizationIds.length ? (
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  {organizationIds.length} selected
                  {organizationIds[0]
                    ? ` · ${
                        orgOptions.find(
                          (o) => o.value === String(organizationIds[0])
                        )?.label || organizationIds[0]
                      }`
                    : ""}
                  {organizationIds.length > 1 ? ` +${organizationIds.length - 1}` : ""}
                </p>
              ) : activeOrgId === "all" || activeOrgId === "*" ? (
                <p className="mt-1 text-[11px] text-[#9CA3AF]">
                  Header is All Organizations — select one or more orgs here.
                </p>
              ) : null}
            </Field>
            <Field>
              <YhMultiSelect
                label="Project"
                showSelectAll
                clearable
                maxVisibleChips={2}
                disabled={!organizationIds.length}
                placeholder={
                  organizationIds.length
                    ? "All projects (optional)…"
                    : "Select organization first…"
                }
                searchPlaceholder="Search projects…"
                options={projectOptions}
                value={projectIds}
                onChange={(values) => {
                  setProjectIds(values.map(String));
                  setPage(0);
                }}
              />
              <p className="mt-1 text-[11px] text-[#6B7280]">
                Without a bulk set: filters leads by project. With a bulk set:
                does not filter (bulk leads may have no project) — pick{" "}
                <span className="font-medium">exactly one</span> project to fill
                template vars (name, address, map) for templates like{" "}
                <span className="font-medium">site_visit_scheduled</span> /
                direction.
              </p>
            </Field>
            <Field>
              <YhMultiSelect
                label="Bulk upload sets"
                showSelectAll
                clearable
                maxVisibleChips={2}
                disabled={!organizationIds.length}
                placeholder={
                  !organizationIds.length
                    ? "Select organization first…"
                    : bulkSetOptions.length
                    ? "All leads — or pick dataset(s)…"
                    : "No upload sets for this org yet"
                }
                searchPlaceholder="Search datasets…"
                options={bulkSetOptions}
                value={bulkSetKeys}
                onChange={(values) => {
                  setBulkSetKeys(values.map(String));
                  setPage(0);
                  setSelectAllMatched(true);
                  setSelectedIds(new Set());
                }}
              />
              {bulkSetKeys.length ? (
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  Preview and queue only leads from the selected dataset
                  {bulkSetKeys.length === 1 ? "" : "s"}.
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  Includes completed and partial uploads with saved leads. On
                  Bulk upload sets, use Publish to leads if a set is missing
                  here.
                </p>
              )}
            </Field>
          </div>
          <Field>
            <YhInput
              label="Broadcast name"
              requiredMark
              value={name}
              onChange={(e: any) => setName(e.target.value)}
              placeholder="e.g. March site visit invite"
            />
          </Field>

          <Field>
            <YhSelect
              label="Template mode"
              value={templateMode}
              onChange={(e: any) =>
                setTemplateMode(
                  e.target.value === "by_registration"
                    ? "by_registration"
                    : "single"
                )
              }
              options={[
                { label: "Single template for everyone", value: "single" },
                {
                  label: "Split by registration (two templates)",
                  value: "by_registration",
                },
              ]}
            />
            <p className="mt-1 text-[11px] text-[#6B7280]">
              Split mode: not-registered get the invite+registration-form
              template; already registered get the invite-only template.
            </p>
          </Field>

          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Field>
              <YhSelect
                label={
                  templateMode === "by_registration"
                    ? "Template — not registered"
                    : "WhatsApp template"
                }
                requiredMark
                value={templateWid}
                disabled={!primaryOrgId || loadingTemplates}
                onChange={(e: any) => setTemplateWid(e.target.value || "")}
                options={[
                  {
                    label: !primaryOrgId
                      ? "Select organization first"
                      : loadingTemplates
                      ? "Loading templates…"
                      : templates.length
                      ? "Select template"
                      : "No templates found",
                    value: "",
                  },
                  ...templates.map((t) => ({ label: t.label, value: t.wid })),
                ]}
              />
            </Field>
            {templateMode === "by_registration" ? (
              <Field>
                <YhSelect
                  label="Template — already registered"
                  requiredMark
                  value={templateWidRegistered}
                  disabled={!primaryOrgId || loadingTemplates}
                  onChange={(e: any) =>
                    setTemplateWidRegistered(e.target.value || "")
                  }
                  options={[
                    {
                      label: loadingTemplates
                        ? "Loading templates…"
                        : "Select template",
                      value: "",
                    },
                    ...templates.map((t) => ({
                      label: t.label,
                      value: t.wid,
                    })),
                  ]}
                />
              </Field>
            ) : (
              <div />
            )}
          </div>
        </div>
      </Section>

      <Section
        title="When to send"
        subtitle="Default is send instantly. Schedule uses India Standard Time (Asia/Kolkata, IST)."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field>
            <label className="yh-label mb-2 block">Run mode</label>
            <div className="flex flex-col gap-2">
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-[#E5E7EB] px-3 py-2.5 hover:bg-[#F9FAFB]">
                <input
                  type="radio"
                  className="mt-0.5 accent-[#A02448]"
                  name="wa-run-mode"
                  checked={runMode === "instant"}
                  onChange={() => setRunMode("instant")}
                />
                <span>
                  <span className="block text-sm font-semibold text-[#111827]">
                    Send instantly
                  </span>
                  <span className="text-[11px] text-[#6B7280]">
                    Queue and start sending as soon as you click Start.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-[#E5E7EB] px-3 py-2.5 hover:bg-[#F9FAFB]">
                <input
                  type="radio"
                  className="mt-0.5 accent-[#A02448]"
                  name="wa-run-mode"
                  checked={runMode === "scheduled"}
                  onChange={() => {
                    setRunMode("scheduled");
                    if (!scheduledLocal) {
                      // Default picker value: next hour in IST
                      const parts = new Intl.DateTimeFormat("en-GB", {
                        timeZone: "Asia/Kolkata",
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                      }).formatToParts(new Date(Date.now() + 60 * 60 * 1000));
                      const get = (t: string) =>
                        parts.find((p) => p.type === t)?.value || "";
                      setScheduledLocal(
                        `${get("year")}-${get("month")}-${get("day")}T${get(
                          "hour"
                        )}:${get("minute")}`
                      );
                    }
                  }}
                />
                <span>
                  <span className="block text-sm font-semibold text-[#111827]">
                    Schedule for later
                  </span>
                  <span className="text-[11px] text-[#6B7280]">
                    Pick date &amp; time in IST (India).
                  </span>
                </span>
              </label>
            </div>
          </Field>
          <Field>
            <label className="yh-label mb-1 block">
              Date &amp; time (IST){runMode === "scheduled" ? " *" : ""}
            </label>
            <input
              type="datetime-local"
              disabled={runMode !== "scheduled"}
              value={scheduledLocal}
              onChange={(e) => setScheduledLocal(e.target.value)}
              className="yh-input w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm text-[#111827] disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF]"
            />
            <p className="mt-1 text-[11px] text-[#6B7280]">
              Timezone locked to <span className="font-medium">Asia/Kolkata (IST)</span>
              {runMode === "scheduled" && scheduledLocal
                ? ` · Will run at ${scheduledLocal.replace("T", " ")} IST`
                : ""}
              .
            </p>
          </Field>
        </div>
      </Section>

      <Section
        title="Audience"
        subtitle="Choose how to build the recipient list, then upload or filter."
      >
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {(
            [
              {
                id: "file_only" as const,
                title: "File only — do not add leads",
                sub: "Direct WhatsApp trigger from CSV/XLSX — never creates CRM leads",
              },
              {
                id: "registration" as const,
                title: "Registration submissions",
                sub: "Phones from registration form fills — never creates CRM leads",
              },
              {
                id: "feedback" as const,
                title: "Feedback submissions",
                sub: "Phones from feedback form fills — never creates CRM leads",
              },
              {
                id: "crm_match" as const,
                title: "CRM leads — filter by stage",
                sub: "Select existing leads (optional file match + stage/source filters)",
              },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                setAudienceMode(opt.id);
                setSubmissionFormId("");
                setPage(0);
                setSelectAllMatched(true);
                setSelectedIds(new Set());
              }}
              className={`rounded-xl border px-3 py-2 text-left text-sm transition ${
                audienceMode === opt.id
                  ? "border-[#A02448] bg-[#F9F0F4] font-semibold text-[#A02448]"
                  : "border-[#E5E7EB] bg-white text-[#374151] hover:border-[#D1D5DB]"
              }`}
            >
              {opt.title}
              <span className="mt-0.5 block text-[11px] font-normal text-[#6B7280]">
                {opt.sub}
              </span>
            </button>
          ))}
        </div>

        {audienceMode === "file_only" ||
        audienceMode === "registration" ||
        audienceMode === "feedback" ? (
          <div className="mb-4 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2.5 text-xs text-[#166534]">
            <strong>Confirmed:</strong>{" "}
            {audienceMode === "file_only"
              ? "File-only mode sends WhatsApp directly to uploaded phones."
              : audienceMode === "registration"
                ? "Uses phones from registration submissions."
                : "Uses phones from feedback submissions."}{" "}
            Contacts are <strong>not</strong> added to Leads.
          </div>
        ) : null}

        {(audienceMode === "registration" || audienceMode === "feedback") && (
          <div className="mb-4 max-w-md">
            <YhSelect
              label={
                audienceMode === "registration"
                  ? "Registration form (optional)"
                  : "Feedback form (optional)"
              }
              clearable
              searchable
              placeholder="All forms in selected org(s)"
              value={submissionFormId}
              options={[
                { label: "All forms", value: "" },
                ...submissionFormOptions,
              ]}
              onChange={(e: any) => {
                setSubmissionFormId(e.target.value || "");
                setPage(0);
              }}
            />
          </div>
        )}

        {audienceMode === "file_only" ? (
          <div className="mb-4 overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E5E7EB] px-3 py-2">
              <p className="text-xs font-semibold text-[#111827]">
                Sample file format
              </p>
              <button
                type="button"
                onClick={downloadSampleAudience}
                className="text-xs font-semibold text-[#A02448] hover:underline"
              >
                Download sample CSV
              </button>
            </div>
            <div className="overflow-x-auto px-3 py-2">
              <table className="w-full min-w-[320px] border-collapse text-left text-[11px] text-[#374151]">
                <thead>
                  <tr className="text-[#6B7280]">
                    <th className="py-1 pr-3 font-semibold">Phone *</th>
                    <th className="py-1 pr-3 font-semibold">Name</th>
                    <th className="py-1 font-semibold">Email</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-0.5 pr-3 font-mono">919876543210</td>
                    <td className="py-0.5 pr-3">Sample Customer</td>
                    <td className="py-0.5">sample@example.com</td>
                  </tr>
                  <tr>
                    <td className="py-0.5 pr-3 font-mono">9876543210</td>
                    <td className="py-0.5 pr-3">Another Contact</td>
                    <td className="py-0.5">—</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-1.5 text-[11px] text-[#6B7280]">
                Required column: <strong>Phone</strong> / Mobile / WhatsApp.
                Optional: Name, Email. Save as .csv or .xlsx.
              </p>
            </div>
          </div>
        ) : null}

        {audienceMode === "file_only" || audienceMode === "crm_match" ? (
        <div className="mb-3 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Field>
            <label className="yh-label mb-1 block">
              Audience file (.csv / .xlsx)
              {audienceMode === "file_only" ? " *" : " (optional)"}
            </label>
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={!organizationIds.length || parsingAudience}
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                e.target.value = "";
                onAudienceFile(f);
              }}
              className="block w-full text-sm text-[#374151] file:mr-3 file:rounded-lg file:border-0 file:bg-[#F9F0F4] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-[#4338CA]"
            />
            <p className="mt-1 text-[11px] text-[#6B7280]">
              {audienceMode === "file_only"
                ? "Upload your list — messages fire on Start; numbers stay out of Leads."
                : "Optional: match file phones to existing CRM leads in the selected org(s)."}
            </p>
            {audienceFileName ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-lg bg-[#F9F0F4] px-2.5 py-1 font-semibold text-[#4338CA]">
                  {audienceFileName}
                </span>
                {audienceStats ? (
                  <span className="text-[#6B7280]">
                    {audienceMode === "file_only"
                      ? `${audienceStats.fileRows ?? audienceContacts.length} contacts (no CRM insert)`
                      : `${audienceStats.matched ?? "—"} matched${
                          audienceStats.unmatched
                            ? ` · ${audienceStats.unmatched} not in CRM`
                            : ""
                        }${
                          audienceStats.fileRows
                            ? ` · ${audienceStats.fileRows} in file`
                            : ""
                        }`}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="font-semibold text-[#B91C1C] hover:underline"
                  onClick={() => {
                    setAudienceContacts([]);
                    setAudienceFileName("");
                    setAudienceStats(null);
                    setPage(0);
                  }}
                >
                  Clear file
                </button>
              </div>
            ) : null}
          </Field>
          {audienceMode === "crm_match" ? (
            <Field>
              <YhSelect
                label="Registration filled filter"
                value={registrationStatus}
                clearable
                placeholder="All leads"
                onChange={(e: any) => {
                  setRegistrationStatus(e.target.value || "");
                  setPage(0);
                }}
                options={[
                  { label: "All leads", value: "" },
                  { label: "Filled registration", value: "filled" },
                  { label: "Did not fill registration", value: "not_filled" },
                ]}
              />
              <p className="mt-1 text-[11px] text-[#6B7280]">
                Use with single template to target only one group. Or use Split
                template mode above for both groups in one broadcast.
              </p>
            </Field>
          ) : (
            <Field>
              <label className="yh-label mb-1 block">What happens next</label>
              <p className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs text-[#4B5563]">
                After <strong>Start broadcast</strong>, each phone is queued and
                marked <strong>sent</strong> or <strong>failed</strong> on the
                detail page. No Lead records are created.
              </p>
            </Field>
          )}
        </div>
        ) : null}
        {audienceMode === "crm_match" ? (
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
            <Field>
              <YhMultiSelect
                label="Stages"
                value={stages}
                clearable
                searchable
                showSelectAll
                placeholder="All stages"
                options={stageOptions}
                onChange={(vals) => {
                  setStages(vals.map(String));
                  setPage(0);
                }}
              />
            </Field>
            <Field>
              <YhSelect
                label="Source"
                value={source}
                clearable
                placeholder="All sources"
                onChange={(e: any) => {
                  setSource(e.target.value || "");
                  setPage(0);
                }}
                options={[
                  { label: "All sources", value: "" },
                  ...sourceOptions,
                ]}
              />
            </Field>
            <Field>
              <YhInput
                label="Search"
                value={search}
                onChange={(e: any) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Name, phone, or email"
              />
            </Field>
          </div>
        ) : null}
      </Section>

      <Section
        title={
          audienceMode === "crm_match" ? "Lead preview" : "Contact preview"
        }
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#374151]">
            <span className="inline-flex items-center rounded-lg bg-[#F9F0F4] px-2.5 py-1 text-xs font-semibold text-[#4338CA]">
              {previewTotal}{" "}
              {audienceMode === "crm_match" ? "matched" : "contacts"}
              {audienceMode === "crm_match" && bulkSetKeys.length
                ? " in selected set(s)"
                : ""}
            </span>
            <span className="text-[#6B7280]">
              {selectAllMatched
                ? audienceMode === "file_only"
                  ? "All file contacts will be queued (not added as leads)"
                  : audienceMode === "registration"
                    ? "All registration phones will be queued (not added as leads)"
                    : audienceMode === "feedback"
                      ? "All feedback phones will be queued (not added as leads)"
                      : bulkSetKeys.length
                        ? "Only leads from the selected upload set(s) will be queued"
                        : "All matched leads will be queued"
                : `${selectedIds.size} selected`}
            </span>
          </div>
          <YhButton
            variant="secondary"
            size="sm"
            onClick={loadPreview}
            disabled={!organizationIds.length || loadingPreview}
          >
            Refresh preview
          </YhButton>
        </div>

        <div className="relative min-h-[320px] overflow-hidden rounded-xl border border-[#F3F4F6]">
          <YhTable
            columns={columns}
            rows={previewRows}
            getRowId={(row) => String(row._id)}
            loading={loadingPreview}
            emptyMessage={
              !organizationIds.length
                ? "Select at least one organization to preview"
                : audienceMode === "file_only"
                  ? audienceContacts.length
                    ? "No contacts to show"
                    : "Upload an XLSX/CSV to preview contacts"
                  : audienceMode === "registration"
                    ? "No registration submissions with phones"
                    : audienceMode === "feedback"
                      ? "No feedback submissions with phones"
                      : "No leads match these filters"
            }
            className="min-h-[320px]"
            page={page}
            pageSize={pageSize}
            totalRows={previewTotal}
            paginationLabel={
              audienceMode === "crm_match" ? "leads" : "contacts"
            }
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
          />
        </div>
      </Section>
    </div>
    </MarketingAccessGate>
  );
};

export default WhatsAppBroadcastCreatePage;
